// "Install app" — one button, two platforms, because the platforms do not agree.
//
// Android/Chrome fires `beforeinstallprompt`, which we hold on to and replay when the
// button is pressed: a real one-tap install. iOS/Safari has no install API at all — Apple
// does not provide one — so there the same button can only show the three taps. Hiding the
// button on iPhone would be worse: that is the half of the team most likely to want the
// icon and least likely to find Share > Add to Home Screen on their own.
//
// (user 2026-10-06: "possible to have a button to let them install? maybe under profile
// there can put install app")
import { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

const PP = "'Poppins', sans-serif";

// Already running from the home screen? Then there is nothing to offer.
const isStandalone = () => {
    try {
        return window.matchMedia?.('(display-mode: standalone)').matches
            || window.navigator.standalone === true;      // iOS's own flag
    } catch { return false; }
};

const isIos = () => {
    try {
        const ua = navigator.userAgent || '';
        // iPadOS 13+ reports itself as a Mac, so the touch count is what separates them.
        return /iPad|iPhone|iPod/.test(ua)
            || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    } catch { return false; }
};

// On iOS, Add to Home Screen exists ONLY in Safari — not Chrome, not the in-app browser
// WhatsApp opens links in. Somebody following the steps in the wrong browser will hunt for
// a menu item that is not there, so the sheet says which browser it needs.
const isIosSafari = () => {
    try {
        const ua = navigator.userAgent || '';
        return isIos() && !/CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line\//.test(ua);
    } catch { return false; }
};

const Step = ({ icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.85 }}>
        <Box sx={{ color: '#1A8CFF', display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#334155' }}>{children}</Typography>
    </Box>
);

export default function InstallApp({ size = 'medium', fullWidth = false }) {
    // The event fires once, early, often before React has mounted — index.js stashes it on
    // window so this component can still find it whenever the person reaches the Profile.
    const [prompt, setPrompt] = useState(() => window.__nexwaveInstallPrompt || null);
    const [installed, setInstalled] = useState(isStandalone);
    const [howTo, setHowTo] = useState(false);

    useEffect(() => {
        const onAvailable = () => setPrompt(window.__nexwaveInstallPrompt || null);
        const onInstalled = () => { setInstalled(true); setPrompt(null); };
        window.addEventListener('nexwave:installable', onAvailable);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('nexwave:installable', onAvailable);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    if (installed) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                <Typography sx={{ fontFamily: PP, fontSize: '0.84rem', color: '#166534', fontWeight: 600 }}>
                    Installed — you are using the app
                </Typography>
            </Box>
        );
    }

    const install = async () => {
        // Android: replay the held event. Anywhere else: show the steps.
        if (!prompt) { setHowTo(true); return; }
        try {
            prompt.prompt();
            const { outcome } = await prompt.userChoice;
            // The event is single-use — Chrome will fire a fresh one if they change their
            // mind, so drop this one either way rather than replaying a spent prompt.
            window.__nexwaveInstallPrompt = null;
            setPrompt(null);
            if (outcome === 'accepted') setInstalled(true);
        } catch {
            setHowTo(true);
        }
    };

    return (
        <>
            <Button
                variant="contained" size={size} onClick={install} startIcon={<InstallMobileIcon />}
                sx={{
                    fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                    boxShadow: 'none', ...(fullWidth ? { width: '100%' } : {}),
                }}
            >
                Install app
            </Button>

            <Dialog open={howTo} onClose={() => setHowTo(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, pr: 6 }}>
                    Add nexwave to your home screen
                    <IconButton onClick={() => setHowTo(false)} size="small"
                        sx={{ position: 'absolute', right: 12, top: 12 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {isIos() ? (
                        <>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#64748B', mb: 1 }}>
                                iPhone and iPad do not allow an install button, so it is three taps in Safari:
                            </Typography>
                            <Step icon={<IosShareIcon sx={{ fontSize: 20 }} />}>
                                Tap <strong>Share</strong> at the bottom of Safari
                            </Step>
                            <Step icon={<AddBoxOutlinedIcon sx={{ fontSize: 20 }} />}>
                                Scroll down and tap <strong>Add to Home Screen</strong>
                            </Step>
                            <Step icon={<CheckCircleIcon sx={{ fontSize: 20 }} />}>
                                Tap <strong>Add</strong> — the icon appears with your other apps
                            </Step>
                            {!isIosSafari() && (
                                <Typography sx={{
                                    fontFamily: PP, fontSize: '0.8rem', color: '#B45309', mt: 1.5,
                                    bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', p: 1.25,
                                }}>
                                    You are not in Safari. Add to Home Screen only exists there — open
                                    this page in Safari first, then follow the steps above.
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', color: '#64748B', mb: 1 }}>
                                Your browser has not offered the install yet. From the browser menu:
                            </Typography>
                            <Step icon={<InstallMobileIcon sx={{ fontSize: 20 }} />}>
                                Open the <strong>⋮</strong> menu and choose <strong>Install app</strong> or
                                {' '}<strong>Add to Home screen</strong>
                            </Step>
                            <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8', mt: 1.5 }}>
                                Chrome only offers it after you have used the site for a moment, and never
                                in a private window.
                            </Typography>
                        </>
                    )}
                    <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8', mt: 2 }}>
                        Installed, it opens full screen with no address bar. It still needs a connection —
                        nothing is stored on the phone to work offline.
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
    );
}
